/**
 * /app/drip-library — browsable catalog of prebuilt drip sequences.
 *
 * Groups all 22 templates by category, lets the operator search, preview
 * every step, and adopt any single template into their account with one
 * click. Adopted templates are still edited via the older /app/drips page
 * (linked in the header).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, X, Home, Users, UserPlus, Clock, MailCheck, Plus,
  Loader2, Check, ExternalLink, BookOpen,
} from "lucide-react";
import { api } from "@/lib/api";

const AUDIENCE_META = {
  seller:  { label: "Seller",   icon: Home,     dot: "bg-emerald-500" },
  buyer:   { label: "Buyer",    icon: Users,    dot: "bg-blue-500" },
  capture: { label: "Capture",  icon: UserPlus, dot: "bg-amber-500" },
};

const ACCENT_TO_CLASSES = {
  amber:   { chip: "bg-amber-50 text-amber-800 border-amber-200",     ring: "hover:border-amber-400" },
  emerald: { chip: "bg-emerald-50 text-emerald-800 border-emerald-200", ring: "hover:border-emerald-400" },
  indigo:  { chip: "bg-indigo-50 text-indigo-800 border-indigo-200",   ring: "hover:border-indigo-400" },
  sky:     { chip: "bg-sky-50 text-sky-800 border-sky-200",           ring: "hover:border-sky-400" },
  rose:    { chip: "bg-rose-50 text-rose-800 border-rose-200",         ring: "hover:border-rose-400" },
};

export default function DripLibrary() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [audience, setAudience] = useState("all");
  const [preview, setPreview] = useState(null);   // template_key currently previewed
  const [previewDetail, setPreviewDetail] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [adopting, setAdopting] = useState(null); // template_key currently being adopted

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/drip/library");
        setGroups(data.groups || []);
        setTotal(data.total || 0);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load library");
      } finally { setLoading(false); }
    })();
  }, []);

  // Fetch preview detail when a template is opened
  useEffect(() => {
    if (!preview) { setPreviewDetail(null); return; }
    setPreviewLoading(true);
    api.get(`/drip/library/${preview}`)
      .then(({ data }) => setPreviewDetail(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load template"))
      .finally(() => setPreviewLoading(false));
  }, [preview]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groups
      .filter((g) => category === "all" || g.key === category)
      .map((g) => ({
        ...g,
        templates: g.templates.filter((t) => {
          if (audience !== "all" && t.audience !== audience) return false;
          if (!q) return true;
          return (
            t.name.toLowerCase().includes(q) ||
            t.description.toLowerCase().includes(q) ||
            t.template_key.toLowerCase().includes(q)
          );
        }),
      }))
      .filter((g) => g.templates.length > 0);
  }, [groups, category, audience, query]);

  const adopt = async (t) => {
    if (t.adopted_campaign_id) {
      navigate(`/app/drips?open=${t.adopted_campaign_id}`);
      return;
    }
    setAdopting(t.template_key);
    try {
      const { data } = await api.post(`/drip/library/${t.template_key}/adopt`);
      toast.success(`Added "${t.name}" to your drips`);
      // Update local state so the button flips to "Already added"
      setGroups((gs) => gs.map((g) => ({
        ...g,
        templates: g.templates.map((tt) =>
          tt.template_key === t.template_key
            ? { ...tt, adopted_campaign_id: data.campaign_id }
            : tt),
      })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Adoption failed");
    } finally { setAdopting(null); }
  };

  // Free-tier: let users browse the library (creates upsell moments), but
  // adopting a template returns 402 from the backend — we catch that and
  // show a Pro-upgrade toast when they click "Add to my drips".

  return (
    <div className="max-w-[1200px] mx-auto p-6 md:p-8" data-testid="drip-library">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] font-black tracking-[0.18em] uppercase text-[var(--primary)] mb-2 flex items-center gap-1.5">
            <BookOpen size={11} /> Drip Library
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
            {total} sequences. <span className="text-[var(--primary)]">One click to enroll.</span>
          </h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            Pre-written email drip campaigns organized by category. Preview any sequence, then add it to your drips
            in one click. Every template is editable after you adopt it.
          </p>
        </div>
        <Link
          to="/app/drips"
          data-testid="drip-library-my-drips-link"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--ink-2)] hover:text-[var(--ink)] border border-[var(--line)] rounded-lg px-3 py-2 hover:border-[var(--primary)]"
        >
          <MailCheck size={14} /> My Drips
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search sequences by name or keyword…"
            data-testid="drip-library-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--line)] focus:border-[var(--primary)] focus:outline-none text-sm"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="drip-library-category-filter"
          className="px-3 py-2 rounded-lg border border-[var(--line)] text-sm font-semibold bg-white"
        >
          <option value="all">All categories</option>
          {groups.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
        </select>
        <select
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          data-testid="drip-library-audience-filter"
          className="px-3 py-2 rounded-lg border border-[var(--line)] text-sm font-semibold bg-white"
        >
          <option value="all">All audiences</option>
          <option value="seller">Seller</option>
          <option value="buyer">Buyer</option>
          <option value="capture">Capture</option>
        </select>
      </div>

      {/* Loading / empty */}
      {loading && (
        <div className="flex items-center gap-2 text-[var(--ink-3)]" data-testid="drip-library-loading">
          <Loader2 size={16} className="animate-spin" /> Loading library…
        </div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="p-10 text-center rounded-2xl border border-dashed border-[var(--line)] text-[var(--ink-3)]">
          No sequences match your filters.
        </div>
      )}

      {/* Category groups */}
      <div className="space-y-10">
        {filtered.map((g) => {
          const accent = ACCENT_TO_CLASSES[g.accent] || ACCENT_TO_CLASSES.emerald;
          return (
            <section key={g.key} data-testid={`drip-library-category-${g.key}`}>
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] font-black tracking-[0.18em] uppercase px-2 py-1 rounded border ${accent.chip}`}>
                    {g.label}
                  </span>
                  <span className="text-xs text-[var(--ink-3)]">
                    {g.templates.length} {g.templates.length === 1 ? "sequence" : "sequences"}
                  </span>
                </div>
                <p className="text-sm text-[var(--ink-2)]">{g.description}</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {g.templates.map((t) => (
                  <TemplateCard
                    key={t.template_key}
                    t={t}
                    accent={accent}
                    onPreview={() => setPreview(t.template_key)}
                    onAdopt={() => adopt(t)}
                    adopting={adopting === t.template_key}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Preview drawer */}
      {preview && (
        <PreviewDrawer
          templateKey={preview}
          detail={previewDetail}
          loading={previewLoading}
          onClose={() => setPreview(null)}
          onAdopt={() => {
            const found = filtered.flatMap((g) => g.templates).find((t) => t.template_key === preview);
            if (found) adopt(found);
          }}
          adopting={adopting === preview}
        />
      )}
    </div>
  );
}

function TemplateCard({ t, accent, onPreview, onAdopt, adopting }) {
  const A = AUDIENCE_META[t.audience] || AUDIENCE_META.seller;
  return (
    <div
      className={`bg-white rounded-xl border border-[var(--line)] p-5 flex flex-col transition ${accent.ring}`}
      data-testid={`drip-library-card-${t.template_key}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-xs font-bold text-[var(--ink-2)]">
          <A.icon size={13} /> {A.label}
        </div>
        <div className="text-xs text-[var(--ink-3)] flex items-center gap-1">
          <Clock size={11} /> {t.duration_days}d · {t.step_count} steps
        </div>
      </div>
      <div className="font-display font-black text-lg leading-tight mb-2">{t.name}</div>
      <p className="text-sm text-[var(--ink-2)] leading-snug flex-1 mb-4">{t.description}</p>
      <div className="flex items-center gap-2 mt-auto">
        <button
          type="button"
          onClick={onPreview}
          data-testid={`drip-library-preview-${t.template_key}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-[var(--line)] text-xs font-bold hover:border-[var(--primary)] hover:bg-[var(--primary)]/5"
        >
          Preview steps
        </button>
        {t.adopted_campaign_id ? (
          <Link
            to={`/app/drips?open=${t.adopted_campaign_id}`}
            data-testid={`drip-library-open-${t.template_key}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
          >
            <Check size={12} /> In My Drips
          </Link>
        ) : (
          <button
            type="button"
            onClick={onAdopt}
            disabled={adopting}
            data-testid={`drip-library-adopt-${t.template_key}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-[var(--primary)] text-[var(--ink)] hover:bg-[var(--primary)]/90 disabled:opacity-60"
          >
            {adopting ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Add to my drips
          </button>
        )}
      </div>
    </div>
  );
}

function PreviewDrawer({ templateKey, detail, loading, onClose, onAdopt, adopting }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="drip-library-preview-drawer">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl border-l border-[var(--line)]">
        <div className="sticky top-0 bg-white border-b border-[var(--line)] p-5 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] font-black tracking-[0.18em] uppercase text-[var(--primary)]">Preview</div>
            <div className="font-display font-black text-xl truncate">{detail?.name || templateKey}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="drip-library-preview-close"
            className="p-2 rounded-lg hover:bg-slate-100"
          >
            <X size={16} />
          </button>
        </div>

        {loading && (
          <div className="p-8 flex items-center gap-2 text-[var(--ink-3)]">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}

        {detail && (
          <div className="p-5 space-y-6">
            <div>
              <p className="text-[var(--ink-2)] leading-relaxed mb-3">{detail.description}</p>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded bg-slate-100 text-[var(--ink-2)] font-semibold">
                  {detail.step_count} steps
                </span>
                <span className="px-2 py-1 rounded bg-slate-100 text-[var(--ink-2)] font-semibold">
                  {detail.duration_days}d duration
                </span>
                <span className="px-2 py-1 rounded bg-slate-100 text-[var(--ink-2)] font-semibold">
                  Audience: {detail.audience}
                </span>
                {detail.auto_exit_on_reply && (
                  <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-800 font-semibold border border-emerald-200">
                    Exits on reply
                  </span>
                )}
                {detail.auto_roll_to_key && (
                  <span className="px-2 py-1 rounded bg-indigo-50 text-indigo-800 font-semibold border border-indigo-200">
                    Auto-rolls to {detail.auto_roll_to_key}
                  </span>
                )}
              </div>
            </div>

            <div className="pt-2 border-t border-[var(--line)]">
              {detail.adopted_campaign_id ? (
                <Link
                  to={`/app/drips?open=${detail.adopted_campaign_id}`}
                  data-testid="drip-library-preview-open"
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 font-bold text-sm hover:bg-emerald-100"
                >
                  <ExternalLink size={14} /> Open in My Drips
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={onAdopt}
                  disabled={adopting}
                  data-testid="drip-library-preview-adopt"
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[var(--primary)] text-[var(--ink)] font-bold text-sm hover:bg-[var(--primary)]/90 disabled:opacity-60"
                >
                  {adopting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Add to my drips
                </button>
              )}
            </div>

            <div className="space-y-4">
              {(detail.steps || []).map((s, i) => (
                <div key={i} className="rounded-lg border border-[var(--line)] overflow-hidden" data-testid={`drip-library-preview-step-${i}`}>
                  <div className="flex items-center justify-between bg-slate-50 px-4 py-2 border-b border-[var(--line)]">
                    <div className="text-xs font-black text-[var(--ink)]">
                      Step {i + 1} · Day {s.day_offset}
                    </div>
                    <div className="text-xs text-[var(--ink-3)]">Email</div>
                  </div>
                  <div className="p-4">
                    <div className="text-sm font-bold mb-2">{s.subject}</div>
                    <pre className="text-sm text-[var(--ink-2)] whitespace-pre-wrap font-sans leading-relaxed">
                      {s.body}
                    </pre>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
